import { Component, input, output, signal, ElementRef, ViewChild, HostListener, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ComponentSize } from '../types';

export interface ContextMenuItem {
  label: string;
  icon?: string;
  route?: string;
  action?: () => void;
  disabled?: boolean;
  divider?: boolean;
  variant?: 'danger' | 'default';
}

@Component({
  selector: 'app-context-menu',
  imports: [CommonModule, RouterModule],
  template: `
    <div 
      #container
      (contextmenu)="onContextMenu($event)"
      class="w-full h-full">
      <ng-content></ng-content>
      
      @if (isOpen()) {
        <div
          #menu
          [class]="menuClasses"
          [style]="menuStyles()"
          role="menu"
          (click)="$event.stopPropagation()">
          @for (item of items(); track item.label) {
            @if (item.divider) {
              <div class="my-1 border-t border-gray-200 dark:border-gray-700"></div>
            } @else if (item.route) {
              <a
                [routerLink]="item.route"
                [class]="getItemClasses(item)"
                (click)="closeMenu()">
                @if (item.icon) {
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                  </svg>
                }
                <span>{{ item.label }}</span>
              </a>
            } @else {
              <button
                type="button"
                [disabled]="item.disabled"
                [class]="getItemClasses(item)"
                (click)="handleAction(item)">
                @if (item.icon) {
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                  </svg>
                }
                <span>{{ item.label }}</span>
              </button>
            }
          }
        </div>
      }
    </div>
  `,
  styles: []
})
export class ContextMenuComponent implements AfterViewInit {
  items = input<ContextMenuItem[]>([]);
  size = input<ComponentSize>('md');

  itemClickEvent = output<ContextMenuItem>();

  @ViewChild('container', { static: false }) container!: ElementRef<HTMLElement>;
  @ViewChild('menu', { static: false }) menu!: ElementRef<HTMLElement>;

  isOpen = signal(false);
  menuStyles = signal<{ [key: string]: string }>({});

  ngAfterViewInit(): void {
    // Position will be set on context menu open
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:contextmenu', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement;
    if (this.container?.nativeElement?.contains(target)) return;
    if (this.menu?.nativeElement?.contains(target)) return;
    this.closeMenu();
  }

  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    this.isOpen.set(true);
    this.updatePosition(event.clientX, event.clientY);
  }

  updatePosition(x: number, y: number): void {
    const menu = this.menu?.nativeElement;
    if (!menu) return;

    const rect = menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = x;
    let top = y;

    // Adjust if menu would overflow viewport
    if (left + rect.width > viewportWidth) {
      left = viewportWidth - rect.width - 8;
    }
    if (top + rect.height > viewportHeight) {
      top = viewportHeight - rect.height - 8;
    }
    if (left < 8) left = 8;
    if (top < 8) top = 8;

    this.menuStyles.set({
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      zIndex: '9999'
    });
  }

  handleAction(item: ContextMenuItem): void {
    if (item.disabled) return;
    if (item.action) {
      item.action();
    }
    this.itemClickEvent.emit(item);
    this.closeMenu();
  }

  closeMenu(): void {
    this.isOpen.set(false);
  }

  getItemClasses(item: ContextMenuItem): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-5 py-3 text-lg',
      xl: 'px-6 py-4 text-xl'
    };

    const classes = [
      'w-full',
      'flex',
      'items-center',
      'gap-2',
      'rounded-md',
      'transition-colors',
      'duration-150',
      'text-left',
      sizeMap[this.size()]
    ];

    if (item.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('hover:bg-gray-100', 'dark:hover:bg-gray-700', 'cursor-pointer');
    }

    if (item.variant === 'danger') {
      classes.push('text-red-600', 'dark:text-red-400', 'hover:bg-red-50', 'dark:hover:bg-red-900/20');
    } else {
      classes.push('text-gray-700', 'dark:text-gray-300');
    }

    return classes.join(' ');
  }

  get menuClasses(): string {
    return 'absolute z-50 min-w-[200px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl py-1';
  }
}

